/**
 * RoomDetail (PPL-44 B3 / B5) — canonical status + next-step view.
 *
 * Used when:
 *   - The inviter taps their own "invited" card (B5) — shows "waiting for
 *     them" copy.
 *   - Any state without a more-specific dedicated screen (paranoid catchall).
 *
 * Subscribes to the root mediation doc via `subscribeToRoom` so state
 * transitions (e.g. invitee accepts → prep) appear immediately.
 *
 * Routing redirects for states that DO have dedicated screens:
 *   - ended   → /rooms/:id/summary
 *   - invited + invitee → /rooms/:id/invite (shouldn't normally land here)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import { deleteRoomForBoth, subscribeToRoom } from '../services/roomService';
import type { Room } from '../types/room';
import '../styles/RoomDetail.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function displayName(m: Room, myUid: string): string {
  // The root doc carries the inviter-typed invitee name (placeholder invites);
  // profiles aren't loaded here, so that's the best we have. Only the inviter's
  // view may use it — the invitee sees the neutral fallback.
  if (myUid && m.inviterUid === myUid && m.inviteeName) {
    return m.inviteeName.split(' ')[0];
  }
  return 'them';
}

// ─── Component ────────────────────────────────────────────────────────────────

const RoomDetail: React.FC = () => {
  const { mediationId } = useParams<{ mediationId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const uid = user?.uid ?? null;

  const [mediation, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cancel-invite (PPL-160) uses its OWN error state so a failure shows inline
  // and doesn't trip the page-level `error` guard that swaps the whole screen.
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // True once we've asked to delete the room, so the subscription can ignore the
  // null snapshot the delete triggers instead of flashing "room not found".
  const deletingRef = useRef(false);

  useEffect(() => {
    if (!mediationId) {
      setError('missing mediation id');
      setLoading(false);
      return;
    }
    const unsub = subscribeToRoom(
      mediationId,
      (data) => {
        // While we're deleting/navigating away, ignore the null snapshot our own
        // delete triggers — otherwise it flashes the full-screen "room not found"
        // error for a frame before the redirect lands (PPL-160 review).
        if (!data && deletingRef.current) return;
        setRoom(data);
        setError(data ? null : 'room not found');
        setLoading(false);
      },
      (err) => {
        setError(err.message ?? 'could not load room');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [mediationId]);

  if (!mediationId) {
    return <Navigate to="/rooms" replace />;
  }

  if (loading) {
    return (
      <div className="mdet-page">
        <div className="mdet-loading">
          <span className="f8-orb f8-orb-sm" />
          <span>loading…</span>
        </div>
      </div>
    );
  }

  if (error || !mediation) {
    return (
      <div className="mdet-page">
        <div className="mdet-error" role="alert">
          {error ?? 'room not found'}
        </div>
        <Link to="/rooms" className="mdet-back-link">
          ← back to rooms
        </Link>
      </div>
    );
  }

  const isInviter = !!uid && mediation.inviterUid === uid;
  const isInvitee = !!uid && mediation.inviteeUid === uid;

  // Hard redirects for states that have dedicated screens
  if (mediation.state === 'ended') {
    return <Navigate to={`/rooms/${mediationId}/summary`} replace />;
  }

  // Invitee should be on the invite screen, not here
  if (mediation.state === 'invited' && isInvitee) {
    return <Navigate to={`/rooms/${mediationId}/invite`} replace />;
  }

  // ─── State-aware copy + CTA ───────────────────────────────────────────────

  type Body = { heading: string; body: string; cta?: { label: string; href: string } };

  function getContent(): Body {
    const id = mediationId!;

    if (mediation!.state === 'invited' && isInviter) {
      return {
        heading: 'invitation sent',
        body: "they haven't responded yet. you'll be notified when they do. nothing to do right now — the next step is theirs.",
      };
    }

    if (mediation!.state === 'prep') {
      return {
        heading: "you're both in prep",
        body: "you each prep with f8 in your own private space. continue the conversation when you're ready.",
        cta: { label: 'continue your prep with f8', href: `/rooms/${id}/prep` },
      };
    }

    if (mediation!.state === 'rules_set') {
      return {
        heading: 'time to set your sharing rules',
        body: 'both sides need to sign off on what f8 can reference in the shared room before you meet.',
        cta: { label: 'set sharing rules', href: `/rooms/${id}/consent` },
      };
    }

    if (mediation!.state === 'joint') {
      return {
        heading: 'the opening is ready',
        body: "f8 has prepared an opening synthesis. head into the opener to read it and mark yourself ready.",
        cta: { label: 'go to the opening', href: `/rooms/${id}/opener` },
      };
    }

    if (mediation!.state === 'ongoing') {
      return {
        heading: 'the room is open',
        body: 'the room is active. return there to continue the conversation.',
        cta: { label: 'open the room', href: `/rooms/${id}/room` },
      };
    }

    if (mediation!.state === 'paused') {
      return {
        heading: 'the room is paused',
        body: 'the room is on hold for now. either side can re-open when ready.',
        cta: { label: 'view the room', href: `/rooms/${id}/room` },
      };
    }

    // Fallback
    return {
      heading: 'your room',
      body: "something is in progress. check back soon.",
    };
  }

  const content = getContent();

  // Let the inviter withdraw a still-pending invitation (PPL-160). The backend
  // allows invited → ended, so this cleanly removes the room for both before the
  // invitee ever accepts. Confirm first — it can't be undone.
  const handleCancelInvite = async () => {
    if (!mediationId || cancelling) return;
    const ok = window.confirm(
      'cancel this invitation? the room will be removed for both of you and can’t be undone.',
    );
    if (!ok) return;
    setCancelError(null);
    setCancelling(true);
    deletingRef.current = true;
    try {
      await deleteRoomForBoth(mediationId);
      navigate('/rooms', { replace: true });
    } catch (e) {
      deletingRef.current = false; // delete failed — resume normal snapshot handling
      setCancelError(e instanceof Error ? e.message : 'could not cancel the invitation.');
      setCancelling(false);
    }
  };

  // State pill class
  const pillClass =
    mediation.state === 'invited'
      ? 'mdet-pill mdet-pill--waiting'
      : mediation.state === 'paused'
        ? 'mdet-pill mdet-pill--paused'
        : 'mdet-pill mdet-pill--active';

  const pillLabel =
    mediation.state === 'invited' && isInviter
      ? 'awaiting response'
      : mediation.state === 'prep'
        ? 'in prep'
        : mediation.state === 'rules_set'
          ? 'setting rules'
          : mediation.state === 'joint'
            ? 'opener ready'
            : mediation.state === 'ongoing'
              ? 'ongoing'
              : mediation.state === 'paused'
                ? 'paused'
                : mediation.state;

  return (
    <div className="mdet-page">
      {/* Header */}
      <div className="mdet-header">
        <button
          type="button"
          className="mdet-back-btn"
          onClick={() => navigate('/rooms')}
          aria-label="back to mediations"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="mdet-header__title-wrap">
          <span className="mdet-header__name">room with {displayName(mediation, uid ?? '')}</span>
          <span className={pillClass}>{pillLabel}</span>
        </div>
      </div>

      {/* Body card */}
      <div className="mdet-card">
        <div className="mdet-card__orb-wrap">
          <span className="f8-orb f8-orb-sm" />
        </div>
        <h2 className="mdet-card__heading">{content.heading}</h2>
        <p className="mdet-card__body">{content.body}</p>

        {content.cta && (
          <Link to={content.cta.href} className="mdet-cta">
            {content.cta.label}
          </Link>
        )}

        {mediation.state === 'invited' && isInviter && (
          <>
            <button
              type="button"
              onClick={handleCancelInvite}
              disabled={cancelling}
              style={{
                marginTop: 16,
                background: 'transparent',
                border: '1px solid rgba(244,63,94,0.45)',
                color: '#fda4af',
                borderRadius: 10,
                padding: '9px 16px',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: cancelling ? 'default' : 'pointer',
                opacity: cancelling ? 0.6 : 1,
              }}
            >
              {cancelling ? 'cancelling…' : 'cancel invitation'}
            </button>
            {cancelError && (
              <p style={{ color: '#fda4af', fontSize: 12, marginTop: 8 }} role="alert">
                {cancelError}
              </p>
            )}
          </>
        )}
      </div>

      {/* Footer link */}
      <div className="mdet-footer">
        <Link to="/rooms" className="mdet-back-link">
          ← all rooms
        </Link>
      </div>
    </div>
  );
};

export default RoomDetail;
